/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomFiltersN.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Menu, Typography, Divider } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { CustomButton, SelectTextField, CustomTextFieldN } from "../index";

function CustomFiltersN({
  anchorEl,
  open,
  handleClose,
  mainOptionsList,
  selectedMainOption,
  selectedMainOptionValue,
  handleFilterClick,
  onReset,
  onApplyNow,
  disabled = false,
}) {
  const [selectedMainField, setSelectedMainField] = useState(
    selectedMainOption || ""
  );

  const [selectedMainFieldValue, setSelectedMainFieldValue] = useState(
    selectedMainOptionValue || ""
  );

  const [searchSelectFieldName, setSearchSelectFieldName] = useState("");

  const [fieldType, setFieldType] = useState("");

  const [optionsList, setOptionsList] = useState([]);

  useEffect(() => {
    setSelectedMainField(selectedMainOption || "");

    setSelectedMainFieldValue(selectedMainOptionValue || "");
  }, [selectedMainOption, selectedMainOptionValue]);

  useEffect(() => {
    if (selectedMainField === selectedMainOption) {
      setSelectedMainFieldValue(selectedMainOptionValue || "");
    } else {
      setSelectedMainFieldValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMainField]);

  useEffect(() => {
    if (selectedMainField) {
      let filterSelectedMainOption = mainOptionsList?.find(
        (el) => el?.value === selectedMainField
      );
      if (filterSelectedMainOption) {
        setSearchSelectFieldName(
          filterSelectedMainOption?.label?.toLowerCase() || ""
        );
        setFieldType(filterSelectedMainOption?.type || "search");
        setOptionsList(filterSelectedMainOption?.value_options_list || []);
      }
    }
  }, [selectedMainField, mainOptionsList]);

  const renderInputField = () => {
    if (fieldType === "select") {
      return (
        <SelectTextField
          size="small"
          label={`Select ${searchSelectFieldName}`}
          optionsList={optionsList}
          selectedValue={selectedMainFieldValue}
          setSelectedValue={setSelectedMainFieldValue}
          variant="standard"
          sltFldStyle={styles.selectTextField}
        />
      );
    }

    return (
      <CustomTextFieldN
        size={"small"}
        label={`Search ${searchSelectFieldName}`}
        placeholder={`Search ${searchSelectFieldName}`}
        type={"text"}
        value={selectedMainFieldValue}
        setValue={setSelectedMainFieldValue}
        variant={"standard"}
        txtFldStyle={styles.customTextFieldN}
      />
    );
  };

  return (
    <Box sx={styles.mainContainer}>
      <CustomButton
        size="small"
        variant="contained"
        btnName="Filter"
        handleOnClick={handleFilterClick}
        btnStyle={styles.customButton}
        startIcon={<FilterAltIcon />}
        disabled={disabled}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 12,
            sx: {
              mt: 0.4,
            },
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Typography sx={{ ...styles.heading, ml: "10px" }}>Filter</Typography>
        <Divider />
        <Box sx={styles.container}>
          <Box>
            <SelectTextField
              size="small"
              label="Select option"
              optionsList={mainOptionsList}
              selectedValue={selectedMainField}
              setSelectedValue={setSelectedMainField}
              variant="standard"
              sltFldStyle={styles.selectTextField}
            />

            <Box>
              {renderInputField()}
            </Box>
          </Box>

          <Box sx={styles.btBtnsContainer}>
            <CustomButton
              size="small"
              variant="contained"
              btnName="Reset All"
              handleOnClick={onReset}
            />
            <CustomButton
              size="small"
              variant="contained"
              btnName="Apply Now"
              disabled={!selectedMainFieldValue}
              handleOnClick={() =>
                onApplyNow(selectedMainField, selectedMainFieldValue)
              }
            />
          </Box>
        </Box>
      </Menu>
    </Box>
  );
}

CustomFiltersN.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  mainOptionsList: PropTypes.array,
  selectedMainOption: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedMainOptionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleFilterClick: PropTypes.func,
  onReset: PropTypes.func,
  onApplyNow: PropTypes.func,
  disabled: PropTypes.bool,
};

CustomFiltersN.defaultProps = {
  anchorEl: null,
  open: false,
  handleClose: () => {},
  mainOptionsList: [],
  selectedMainOption: "",
  selectedMainOptionValue: "",
  handleFilterClick: () => {},
  onReset: () => {},
  onApplyNow: () => {},
  disabled: false,
};

export default CustomFiltersN;

const styles = {
  mainContainer: {
  },
  container: {
    px: 1.4,
  },
  btBtnsContainer: {
    display: "flex",
    justifyContent: "space-between",
    mt: 3,
    mb: 0.4,
  },
  selectTextField: {
    width: "250px",
    mt: 1,
  },
  customTextFieldN: {
    width: "250px",
    mt: 1,
  },
  heading: {
    fontWeight: "bold",
  },
};
